import { Controller } from "react-hook-form";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/inputs/TextInput";
import { Button } from "../../../components/button";
import { useCallback, useEffect, useState } from "react";
import VisitPurposeTable from "../../../components/table/VisitPurposeTable";
import {
  useAddUpdateVisitPurpose,
  useIsVisitPurposeExists,
} from "../../../hooks/user/mutation";
import toast, { Toaster } from "react-hot-toast";
import useAuthStore from "../../../store/Auth";
import { useGetVisitPurposeById } from "../../../hooks/user/query";
import { VisitPurposeSchema } from "../../../schemas/visitPurposeSchema";
import { useGetAllVisitPurposes } from "../../../hooks/common/query";
import { PAGE_SIZE } from "../../../constants/common";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";

interface VisitPurposeData {
  purposeEn: string;
  purposerAr: string;
  visitorType?: string;
}

const VisitPurpose = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
  } = useFormWithSchema(VisitPurposeSchema);

  const user = useAuthStore((state) => state.user);

  const [selectedPurposeId, setSelectedPurposeId] = useState(0);
  const [allVisitPurposes, setAllVisitPurposes] = useState([]);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  // const { data: visitRequestTypes } = useGetAllVisitRequestTypes();
  const { data: visitPurposes } = useGetAllVisitPurposes({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });
  const { mutateAsync: AddUpdateVisitPurpose } = useAddUpdateVisitPurpose();
  const { mutateAsync: IsVisitPurposeExists } = useIsVisitPurposeExists();

  useEffect(() => {
    if (visitPurposes) {
      setAllVisitPurposes(visitPurposes.data || []);
      setPages(visitPurposes.pagination?.pages || 1);
      setCount(visitPurposes.pagination?.count || 0);
    }
  }, [setPages, visitPurposes]);

  const { data: visitPurpose } = useGetVisitPurposeById({
    encryptedId: selectedPurposeId ? selectedPurposeId.toString() : "",
  });

  useEffect(() => {
    if (visitPurpose) {
      setValue("purposeEn", visitPurpose.s_PurposeEn);
      setValue("purposerAr", visitPurpose.s_PurposeAr);
      setValue("visitorType", visitPurpose.n_VisitRequestTypeID);
    }
  }, [visitPurpose, setValue]);

  // const visitReqTypes = useMemo(
  //   () =>
  //     mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", visitRequestTypes),
  //   [visitRequestTypes]
  // );

  const onSubmit = useCallback(
    async (data: VisitPurposeData) => {
      if (selectedPurposeId === 0) {
        try {
          const isExistBody = {
            // n_VisitRequestTypeID: Number(data.visitorType),
            s_PurposeEn: data.purposeEn,
            s_PurposeAr: data.purposerAr,
          };
          const { isExists } = await IsVisitPurposeExists(isExistBody);

          if (isExists) {
            toast.error(t("visitPurposeExists"));
            return;
          }
        } catch (error) {
          toast.error(t("somethingWentWrong"));
          console.error("Error checking if visit purpose exists", error);
          return;
        }
      }

      try {
        const body = {
          N_PurposeID: visitPurpose ? visitPurpose.n_PurposeID : 0,
          S_PurposeEn: data.purposeEn,
          S_PurposeAr: data.purposerAr,
          // N_VisitRequestTypeID: Number(data.visitorType),
          N_LocationID: user?.n_LocationID,
        };

        await AddUpdateVisitPurpose(body);

        setSelectedPurposeId(0);

        toast.success(
          !visitPurpose ? t("visitPurposeAdded") : t("visitPurposeUpdated")
        );

        resetField("purposeEn");
        resetField("purposerAr");
        resetField("visitorType");
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error(
          !visitPurpose
            ? "Error adding visit purpose"
            : "Error updating detail code",
          error
        );
      }
    },
    [
      selectedPurposeId,
      IsVisitPurposeExists,
      t,
      visitPurpose,
      user?.n_LocationID,
      AddUpdateVisitPurpose,
      resetField,
    ]
  );

  const onClear = useCallback(() => {
    resetField("purposeEn");
    resetField("purposerAr");
    resetField("visitorType");
  }, [resetField]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("visitPurpose")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col gap-3 md:gap-6">
            <div className="grid md:grid-cols-3 gap-3 lg:gap-6">
              <Controller
                name="purposeEn"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("purposeEnglish")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                )}
              />

              <Controller
                name="purposerAr"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("purposeArabic")}
                    onChange={onChange}
                    value={value || ""}
                    required
                    error={errors?.[name]?.message}
                  />
                )}
              />

              {/* <Controller
              name="visitorType"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <ComboboxInput
                    options={visitReqTypes}
                    value={
                      value
                        ? visitReqTypes?.find(
                            (item) => item.value === Number(value)
                          )?.labelEn
                        : ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      clearErrors(name);
                    }}
                    error={errors?.[name]?.message}
                    label={t("visitorType")}
                    required
                  />
                );
              }}
            /> */}
            </div>
            <div className="flex items-center gap-2">
              <Button>{t("save")}</Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>

          <VisitPurposeTable
            visitPurposes={allVisitPurposes}
            setSelectedPurposeId={setSelectedPurposeId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>
    </>
  );
};

export default VisitPurpose;
